import {
  AdopterDto,
  AdopterDtoAdoptionFormHadAnimalBefore,
  AdopterDtoAdoptionFormMainHomeType,
  AdopterDtoAdoptionFormOwnAnimalUsedToAnimals,
  AdopterDtoAdoptionFormPetIntendedForSelfOrGift,
  AdopterDtoAdoptionFormPlanForPetDuringRelocation,
  AdopterDtoAdoptionFormSex,
  AdopterDtoAdoptionFormSterilizedOrNeutered,
  AdopterDtoAdoptionFormWorkflow,
  AdopterDtoAlreadyHasAnimalOrChildBaby,
  AdopterDtoFirstContact,
  AdopterDtoPreferenceSex,
  AdopterDtoPreferenceSterilizedOrNeutered,
  AdopterDtoProfileHasBeenAccepted,
} from '../../interactors/gen/backendClient'
import { colors } from '../../views/theme'

export const fullName = (adopter: { firstName: string; lastName: string }) => {
  return `${adopter.firstName} ${adopter.lastName}`
}

type AdopterPropertyWithLabel = Exclude<keyof AdopterDto, 'customerId'>

export const getReadableAdopterFieldLabel = (field: AdopterPropertyWithLabel): string => {
  const map: Record<AdopterPropertyWithLabel, string> = {
    id: 'ID',
    firstName: 'Prénom',
    lastName: 'Nom',
    email: 'Email',
    phoneNumber: 'Numero de Télépone',
    address: 'Adresse',
    postalCode: 'Code Postal',
    city: 'Ville',
    country: 'Pays',
    preferences: 'Préférences',
    profileHasBeenAccepted: 'Profil accepté ?',
    firstContact: 'Premier contact',
    adoptionForm: "Formulaire d'adoption",
    followUp: 'Suivi',
    desiredAnimal: "Souhait d'adoption",
    adoptionAttempts: "Procédures d'adoption",
    coordinates: 'Coordonnées',
    isDeleted: 'Supprimé',
    displayPublicly: 'Profil visible auprès des autres membres',
    createdWithGoogle: 'Créé avec Google',
    defaultVeterinary: 'Vétérinaire référent',
    tutorialStep: 'Etape du tutoriel',
    verifiedUserInformation: 'Informations vérifiées',
    civility: 'Civilité',
    nationality: 'Nationalité',
    secondPhoneNumber: 'Deuxième numéro de téléphone',
  }

  return map[field]
}

export const getReadableAdopterFirstContact = (firstContact: AdopterDtoFirstContact): string => {
  const map: Record<AdopterDtoFirstContact, string> = {
    [AdopterDtoFirstContact.WithPhone]: 'Oui (téléphone)',
    [AdopterDtoFirstContact.Physical]: 'Oui (physique)',
    [AdopterDtoFirstContact.ToDo]: 'A faire',
  }

  return map[firstContact]
}

export const getColorForAdopterFirstContact = (firstContact: AdopterDtoFirstContact): string => {
  const map: Record<AdopterDtoFirstContact, string> = {
    [AdopterDtoFirstContact.WithPhone]: '#82CFFF',
    [AdopterDtoFirstContact.Physical]: '#D2E9F7',
    [AdopterDtoFirstContact.ToDo]: '#FFE7AD',
  }

  return map[firstContact]
}

export const getReadableAdopterProfileHasBeenAccepted = (
  profileHasBeenAccepted: AdopterDtoProfileHasBeenAccepted
): string => {
  const map: Record<AdopterDtoProfileHasBeenAccepted, string> = {
    [AdopterDtoProfileHasBeenAccepted.Accepted]: "Accepté par l'asso",
    [AdopterDtoProfileHasBeenAccepted.Refused]: "Refusé par l'asso",
    [AdopterDtoProfileHasBeenAccepted.Waiting]: 'Demande en attente',
    [AdopterDtoProfileHasBeenAccepted.FormSent]: 'Formulaire envoyé',
    [AdopterDtoProfileHasBeenAccepted.FormToSent]: 'Formulaire à envoyer',
    [AdopterDtoProfileHasBeenAccepted.FormAccepted]: 'Formulaire accepté',
    [AdopterDtoProfileHasBeenAccepted.FormRefused]: 'Formulaire refusé',
    [AdopterDtoProfileHasBeenAccepted.Blacklisted]: 'Blacklisté',
  }

  return map[profileHasBeenAccepted]
}

export const getColorForAdopterProfileHasBeenAccepted = (
  profileHasBeenAccepted: AdopterDtoProfileHasBeenAccepted
): string => {
  const map: Record<AdopterDtoProfileHasBeenAccepted, string> = {
    [AdopterDtoProfileHasBeenAccepted.Accepted]: '#5BA578',
    [AdopterDtoProfileHasBeenAccepted.Refused]: '#BC8076',
    [AdopterDtoProfileHasBeenAccepted.Waiting]: colors.yellowSemiLight,
    [AdopterDtoProfileHasBeenAccepted.FormSent]: colors.blueSky,
    [AdopterDtoProfileHasBeenAccepted.FormToSent]: colors.lightGrey,
    [AdopterDtoProfileHasBeenAccepted.FormAccepted]: '#5BA578',
    [AdopterDtoProfileHasBeenAccepted.FormRefused]: '#BC8076',
    [AdopterDtoProfileHasBeenAccepted.Blacklisted]: 'black',
  }

  return map[profileHasBeenAccepted]
}

export const getReadableAdopterPreferenceSex = (preferenceSex: AdopterDtoPreferenceSex): string => {
  const map: Record<AdopterDtoPreferenceSex, string> = {
    [AdopterDtoPreferenceSex.Male]: 'Mâle',
    [AdopterDtoPreferenceSex.Female]: 'Femelle',
    [AdopterDtoPreferenceSex.NoPreferences]: 'Pas de préférence',
  }

  return map[preferenceSex]
}

export const getReadableAdopterPreferenceSterilized = (
  profileHasBeenAccepted: AdopterDtoPreferenceSterilizedOrNeutered
): string => {
  const map: Record<AdopterDtoPreferenceSterilizedOrNeutered, string> = {
    [AdopterDtoPreferenceSterilizedOrNeutered.Yes]: 'Oui',
    [AdopterDtoPreferenceSterilizedOrNeutered.No]: 'Non',
    [AdopterDtoPreferenceSterilizedOrNeutered.NoPreferences]: 'Pas de préférence',
  }

  return map[profileHasBeenAccepted]
}

export const getReadableAdopterAdoptionFormSex = (sex: AdopterDtoAdoptionFormSex): string => {
  const map: Record<AdopterDtoAdoptionFormSex, string> = {
    [AdopterDtoAdoptionFormSex.Male]: 'Mâle',
    [AdopterDtoAdoptionFormSex.Female]: 'Femelle',
    [AdopterDtoAdoptionFormSex.NoPreferences]: 'Pas de préférence',
  }

  return map[sex]
}

export const getReadableAdopterAdoptionFormSterilizedOrNeutered = (
  sterilizedOrNeutered: AdopterDtoAdoptionFormSterilizedOrNeutered
): string => {
  const map: Record<AdopterDtoAdoptionFormSterilizedOrNeutered, string> = {
    [AdopterDtoAdoptionFormSterilizedOrNeutered.SterilizedOrNeutered]: 'Stérilisé/Castré',
    [AdopterDtoAdoptionFormSterilizedOrNeutered.NotSterilizedOrNeutered]: 'Pas stérilisé/Pas castré',
    [AdopterDtoAdoptionFormSterilizedOrNeutered.NoPreferences]: 'Pas de préférence',
  }

  return map[sterilizedOrNeutered]
}

export const getReadableAdopterAdoptionFormHadAnimalBefore = (
  options: AdopterDtoAdoptionFormHadAnimalBefore
): string => {
  const map: Record<AdopterDtoAdoptionFormHadAnimalBefore, string> = {
    [AdopterDtoAdoptionFormHadAnimalBefore.SameAnimal]: 'Oui, le même animal',
    [AdopterDtoAdoptionFormHadAnimalBefore.OtherAnimal]: 'Oui, un autre animal',
    [AdopterDtoAdoptionFormHadAnimalBefore.No]: 'Non, il y a un début à tout',
  }

  return map[options]
}

export const getReadableAdopterAdoptionFormYesNo = (options: AdopterDtoAdoptionFormOwnAnimalUsedToAnimals): string => {
  const map: Record<AdopterDtoAdoptionFormOwnAnimalUsedToAnimals, string> = {
    [AdopterDtoAdoptionFormOwnAnimalUsedToAnimals.Yes]: 'Oui',
    [AdopterDtoAdoptionFormOwnAnimalUsedToAnimals.No]: 'Non',
  }

  return map[options]
}

export const getReadableAdopterAdoptionFormMainHomeType = (options: AdopterDtoAdoptionFormMainHomeType): string => {
  const map: Record<AdopterDtoAdoptionFormMainHomeType, string> = {
    [AdopterDtoAdoptionFormMainHomeType.HouseWithGardenOrCourtyard]: 'Dans une maison avec cour ou jardin',
    [AdopterDtoAdoptionFormMainHomeType.HouseWithoutGardenOrCourtyard]: 'Dans une maison sans cour ou jardin',
    [AdopterDtoAdoptionFormMainHomeType.ApartmentWithBalcony]: 'En appartement avec balcon',
    [AdopterDtoAdoptionFormMainHomeType.ApartmentWithoutBalcony]: 'En appartement sans balcon',
  }

  return map[options]
}

export const getReadableAdopterAdoptionFormWorkflow = (options: AdopterDtoAdoptionFormWorkflow): string => {
  const map: Record<AdopterDtoAdoptionFormWorkflow, string> = {
    [AdopterDtoAdoptionFormWorkflow.WorkFromHome]: "A domicile: télétravail, recherche d'emploi, retraite, étudiant",
    [AdopterDtoAdoptionFormWorkflow.Teleworking]: 'En télétravail 2/3 jours par semaine',
    [AdopterDtoAdoptionFormWorkflow.WorkOutside]: 'Tout le temps en dehors de mon domicile',
    [AdopterDtoAdoptionFormWorkflow.CanGoToWorkWithAnimal]: 'Je peux aller au travail avec mon animal',
  }

  return map[options]
}

export const getReadableAdopterAdoptionFormPlanForPetDuringRelocation = (
  options: AdopterDtoAdoptionFormPlanForPetDuringRelocation
): string => {
  const map: Record<AdopterDtoAdoptionFormPlanForPetDuringRelocation, string> = {
    [AdopterDtoAdoptionFormPlanForPetDuringRelocation.TakeAnimalWithMe]: "Je l'emmènerai avec moi",
    [AdopterDtoAdoptionFormPlanForPetDuringRelocation.LeaveAnimalWithSomeone]:
      'Je le confierai à une personne de confiance',
    [AdopterDtoAdoptionFormPlanForPetDuringRelocation.LeaveAnimalInShelter]:
      "Je contacterai un refuge pour qu'il trouve une nouvelle famille",
    [AdopterDtoAdoptionFormPlanForPetDuringRelocation.LeaveAnimalToWildState]: 'Je le rendrai à son état sauvage',
  }

  return map[options]
}

export const getReadableAdopterAdoptionFormPetIntendedForSelfOrGift = (
  options: AdopterDtoAdoptionFormPetIntendedForSelfOrGift
): string => {
  const map: Record<AdopterDtoAdoptionFormPetIntendedForSelfOrGift, string> = {
    [AdopterDtoAdoptionFormPetIntendedForSelfOrGift.Self]: 'Pour moi et mon foyer',
    [AdopterDtoAdoptionFormPetIntendedForSelfOrGift.Gift]: 'En cadeau à une autre personne',
  }

  return map[options]
}

export const getReadableAdopterAdoptionFormCounter = (options: AdopterDtoAlreadyHasAnimalOrChildBaby): string => {
  const map: Record<AdopterDtoAlreadyHasAnimalOrChildBaby, string> = {
    [AdopterDtoAlreadyHasAnimalOrChildBaby.None]: '0',
    [AdopterDtoAlreadyHasAnimalOrChildBaby.One]: '1',
    [AdopterDtoAlreadyHasAnimalOrChildBaby.Two]: '2',
    [AdopterDtoAlreadyHasAnimalOrChildBaby.Three]: '3',
    [AdopterDtoAlreadyHasAnimalOrChildBaby.Four]: '4',
    [AdopterDtoAlreadyHasAnimalOrChildBaby.Five]: '5',
    [AdopterDtoAlreadyHasAnimalOrChildBaby.MoreThanFive]: '5+',
  }

  return map[options]
}
